import { useTranslation } from 'react-i18next';
import AnalyticsTable from '@customer/components/tables/analytics-table/AnalyticsTable';
import AnalyticsSubPage from '@customer/templates/health-sub-page/AnalyticsSubPage';
import { useCallback, useMemo, useState } from 'react';
import {
  AssetBasicModel,
  AssetListScenarios,
  PerformanceGIAssetsFilters,
} from '@customer/store/apis/assetsApi';
import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import { AssetInteractionsAccordion, AssetSidePanelTab } from '@customer/store/slices/globalSlice';
import usePerformanceSubPage from '@customer/templates/performance-sub-page/usePerformanceSubPage';
import {
  columnDefAssetGroup,
  columnDefAssetName,
  columnDefDeviceType,
  columnDefGeofenceInteractionsTotal,
  columnDefObjectType,
  columnDefSoftwareStatus,
} from '@customer/components/tables/grid/columnDefs';
import { ColDef } from 'ag-grid-community';
import ChartScenarios from '@customer/components/charts/chart-scenarios/ChartScenarios';
import { ChartColorScheme, ScenarioConfig } from '@customer/components/charts/config';
import { PerformanceGIScenarioQueryParams, StatsScenario } from '@customer/store/apis/scenariosApi';
import HistogramChartScenario from '@customer/components/charts/histogram-chart-scenario/HistogramChartScenario';
import BarChartByGeofenceInteractionType from '@customer/components/charts/by-geofence-interaction/BarChartByGeofenceInteractionType';
import AreaChartScenario from '@customer/components/charts/area-chart-scenario/AreaChartScenario';
import { FilterGroup } from '@customer/filters/types';
import GeofenceInteractionsOverview from './components/overview/GeofenceInteractionsOverview';
import { useGetGIHeatmapQuery } from '@customer/store/apis/heatmapsApi';
import LocationFilter from '@customer/components/maps/location-filter/LocationFilter';
import useLocationFilterController from '@customer/components/maps/location-filter/useLocationFilterController';
import BarChartByObjectType from '@customer/components/charts/by-object-type/BarChartByObjectType';
import PerformanceTimeRange from '@customer/components/inputs/performance-time-range/PerformanceTimeRange';
import BarChartByAssetGroup from '@customer/components/charts/by-asset-group/BarChartByAssetGroup';

const tableSliceKey = 'AssetsPerformanceGeofenceInteractions';
const API_TABLE_LIMIT = 20;
const defaultColDef: ColDef<AssetBasicModel> = { sortable: false };

export function GeofenceInteractions() {
  const { t } = useTranslation();

  const [columnDefs] = useState<ColDef<AssetBasicModel>[]>([
    { ...columnDefAssetName(t), maxWidth: 150 },
    { ...columnDefObjectType(t), maxWidth: 150 },
    { ...columnDefDeviceType(t), maxWidth: 110 },
    { ...columnDefAssetGroup(t) },
    { ...columnDefGeofenceInteractionsTotal(t), maxWidth: 110 },
    { ...columnDefSoftwareStatus(t), maxWidth: 150 },
  ]);

  const { assets, hasData, noDataDescription, countAll, countFilter, onRowClick } =
    usePerformanceSubPage<PerformanceGIAssetsFilters>({
      scenario: AssetListScenarios.PerformanceGeofenceInteractions,
      filterGroup: FilterGroup.PerformanceGI,
      sidePanelTab: AssetSidePanelTab.Interactions,
      sidePanelDefaultAccordion: AssetInteractionsAccordion.GeofenceInteractions,
    });

  const getDataSetLabel = useCallback(
    (label: string) => {
      return t('gi.counts');
    },
    [t],
  );

  const scenarioConfigs = useMemo<ScenarioConfig[]>(
    () => [
      {
        title: t('ByTimeRange'),
        ChartComponent: AreaChartScenario<PerformanceGIScenarioQueryParams>,
        scenario: StatsScenario.GeofenceInteractionsByTimeRange,
        hideResultsLimited: true,
        enableCustomTimeRange: true,
      },
      {
        title: t('ByTimeOfDay'),
        ChartComponent: HistogramChartScenario<PerformanceGIScenarioQueryParams>,
        scenario: StatsScenario.GeofenceInteractionsByTimeOfDay,
        hideResultsLimited: true,
      },
      {
        title: t('ByAssetGroup'),
        ChartComponent: BarChartByAssetGroup<PerformanceGIScenarioQueryParams>,
        scenario: StatsScenario.GeofenceInteractionsByAssetGroup,
      },
      {
        title: t('ByObjectType'),
        ChartComponent: BarChartByObjectType<PerformanceGIScenarioQueryParams>,
        scenario: StatsScenario.GeofenceInteractionsByObjectType,
      },
      {
        title: t('ByInteractionType'),
        ChartComponent: BarChartByGeofenceInteractionType<PerformanceGIScenarioQueryParams>,
        scenario: StatsScenario.GeofenceInteractionsByInteractionType,
        hideResultsLimited: true,
      },
    ],
    [t],
  );

  const locationFilterProps = useLocationFilterController({
    filterGroup: FilterGroup.PerformanceGI,
    useHeatmapQuery: useGetGIHeatmapQuery,
  });

  return (
    <AnalyticsSubPage
      countAll={countAll}
      countFilter={countFilter}
      filters={FilterGroup.PerformanceGI}
      header={<PerformanceTimeRange disabled={false} />}
      content={
        <>
          <GeofenceInteractionsOverview />

          <ChartScenarios
            title={t('GeofenceInteractions')}
            scenarioConfigs={scenarioConfigs}
            filterGroup={FilterGroup.PerformanceGI}
            colorScheme={ChartColorScheme.Blue}
            getDataSetLabel={getDataSetLabel}
            tooltipTransKey="InteractionsWithCount"
            includeTimeRange
          />

          <LocationFilter {...locationFilterProps} />

          <AnalyticsTable
            title={t('Assets')}
            hasData={hasData}
            noDataDescription={noDataDescription}
            apiResultLimit={API_TABLE_LIMIT}
          >
            <GridControlled<AssetBasicModel>
              sliceKey={tableSliceKey}
              rowData={assets}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              noRowsMessage={t('No assets')}
              onRowClick={onRowClick}
            />
          </AnalyticsTable>
        </>
      }
    />
  );
}

export default GeofenceInteractions;
